'use client'

import { themeHoverBackgroundMap } from '@mntn-dev/ui-theme'
import { themeBorderColorMap } from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import { mergeRefs } from '@mntn-dev/utilities'
import {
  type MouseEvent,
  type PropsWithChildren,
  forwardRef,
  useRef,
} from 'react'
import { useScrollIntoView } from '../../hooks/use-scroll-into-view.ts'
import { type TestIds, getTestProps } from '../../utils/testing.ts'

export type BladeCommonProps = PropsWithChildren<
  TestIds & {
    className?: string
    disabled?: boolean
    onClick?: (e: MouseEvent<HTMLDivElement>) => void
    scroll?: boolean
    border?: boolean
  }
>

export const BladeCommon = forwardRef<HTMLDivElement, BladeCommonProps>(
  (
    {
      border,
      children,
      className,
      dataTestId,
      dataTrackingId,
      disabled,
      onClick,
      scroll,
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement | undefined>(undefined)
    const { mergedRef: scrollRef } = useScrollIntoView<HTMLDivElement>({
      scroll,
      innerRef,
    })

    const mergedRef = mergeRefs(ref, scrollRef)

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      onClick?.(e)
    }

    return (
      <div
        className={cn(
          'flex w-full max-w-full items-center',
          {
            [cn('cursor-pointer', themeHoverBackgroundMap.tertiary)]: !!onClick,
            'opacity-50 pointer-events-none': disabled,
            [cn('border rounded-md', themeBorderColorMap.muted)]: border,
          },
          className
        )}
        ref={mergedRef}
        onClick={handleClick}
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        {children}
      </div>
    )
  }
)
